/**
 * Reading a lost-race refusal (G4).
 *
 * `/client/` exposes no ETag on agents, so the platform's own `updated` timestamp
 * is the version token: the server re-reads the agent immediately before writing
 * and returns 409 if it moved. The body carries who moved it, which is the part
 * worth surfacing — "someone else changed this" leaves an operator with nothing
 * to do, and a name leaves them with someone to ask.
 *
 * Kept separate from the component so the shape of that body is asserted in a
 * test rather than trusted in a template.
 */

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * The message to show, or null if this failure was not a conflict.
 *
 * Null rather than a fallback string: the caller shows the normal error for
 * anything else, and a conflict reader that quietly claimed every failure was a
 * conflict would hide the real one.
 */
export function conflictOf(failure: unknown): string | null {
  const outer = asRecord(failure);
  if (outer === null) return null;

  const status = outer['status'];
  if (status !== 409) return null;

  // RTK Query nests the response body under `data`; Nest puts our object under
  // `message` when it is thrown as an exception body. Both are checked because
  // the shape depends on how the exception was constructed, and a message that
  // only appeared for one of them would be a message that mostly did not appear.
  const body = asRecord(outer['data']) ?? {};
  const nested = asRecord(body['message']);
  const source = nested ?? body;

  const message = source['message'];
  if (typeof message === 'string' && message !== '') return message;

  const by = source['actualSirmaUpdatedBy'];
  return typeof by === 'string' && by !== ''
    ? `${by} changed this agent while you were editing it. Reload before saving.`
    : 'This agent changed while you were editing it. Reload before saving.';
}
